import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Permission, Role, UserRole } from '@/types';
import { useAuthStore } from './authStore';
import api from '@services/api';

// 角色权限映射（与后端保持一致）
const ROLE_PERMISSIONS: Record<string, string[]> = {
  admin: [
    'project:view', 'project:create', 'project:edit', 'project:delete', 'project:manage_members',
    'task:view', 'task:create', 'task:edit', 'task:delete', 'task:assign',
    'user:view', 'user:create', 'user:edit', 'user:delete',
    'role:manage', 'permission:manage',
    'report:view', 'report:create', 'report:edit', 'report:delete',
    'issue:view', 'issue:create', 'issue:edit', 'issue:delete',
    'resource:view', 'resource:create', 'resource:delete',
    'team:view', 'team:create', 'team:edit', 'team:delete',
    'system:admin',
  ],
  manager: [
    'project:view', 'project:create', 'project:edit', 'project:manage_members',
    'task:view', 'task:create', 'task:edit', 'task:delete', 'task:assign',
    'user:view',
    'report:view', 'report:create',
    'issue:view', 'issue:create', 'issue:edit',
    'resource:view', 'resource:create',
    'team:view', 'team:edit',
  ],
  developer: [
    'project:view',
    'task:view', 'task:create', 'task:edit',
    'report:view', 'report:create',
    'issue:view', 'issue:create', 'issue:edit',
    'resource:view',
  ],
  tester: [
    'project:view',
    'task:view', 'task:create',
    'report:view', 'report:create',
    'issue:view', 'issue:create', 'issue:edit',
    'resource:view',
  ],
  viewer: [
    'project:view',
    'task:view',
    'report:view',
    'issue:view',
    'resource:view',
    'team:view',
  ],
  member: [
    'project:view',
    'task:view', 'task:create', 'task:edit',
    'report:view', 'report:create',
  ],
};

interface PermissionState {
  // 状态
  permissions: Permission[];
  roles: Role[];
  userRoles: UserRole[];
  loading: boolean;
  error: string | null;
  initialized: boolean;

  // 方法
  // 权限管理
  fetchPermissions: () => Promise<void>;
  createPermission: (permission: Omit<Permission, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Permission>;
  updatePermission: (id: string, permission: Partial<Permission>) => Promise<Permission>;
  deletePermission: (id: string) => Promise<void>;

  // 角色管理
  fetchRoles: () => Promise<void>;
  createRole: (role: Omit<Role, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Role>;
  updateRole: (id: string, role: Partial<Role>) => Promise<Role>;
  deleteRole: (id: string) => Promise<void>;

  // 用户角色管理
  fetchUserRoles: () => Promise<void>;
  assignRoleToUser: (userId: string, roleId: string) => Promise<UserRole>;
  removeRoleFromUser: (userId: string, roleId: string) => Promise<void>;

  // 权限检查
  hasPermission: (permissionCode: string) => boolean;
  hasRole: (roleCode: string) => boolean;

  // 初始化
  initialize: () => Promise<void>;

  // 重置状态
  reset: () => void;
}

export const usePermissionStore = create<PermissionState>()(
  devtools(
    (set, get) => ({
      // 初始状态
      permissions: [],
      roles: [],
      userRoles: [],
      loading: false,
      error: null,
      initialized: false,

      // 初始化权限数据
      initialize: async () => {
        if (get().initialized) return;
        await get().fetchPermissions();
        await get().fetchRoles();
        set({ initialized: true });
      },

      // 权限管理
      fetchPermissions: async () => {
        set({ loading: true, error: null });
        try {
          // 调用 API 获取权限列表 - 从角色权限映射中构建
          const rolesResponse = await api.get('/roles');
          let rolesData = rolesResponse.data;

          // 处理可能的响应格式（data 可能在 data 字段中）
          if (rolesData && typeof rolesData === 'object' && !Array.isArray(rolesData) && Array.isArray(rolesData.data)) {
            rolesData = rolesData.data;
          }

          // 确保 rolesData 是数组
          if (!Array.isArray(rolesData)) {
            console.warn('Roles data is not an array, using fallback:', rolesData);
            // 使用本地定义的角色映射作为 fallback
            set({
              roles: Object.entries(ROLE_PERMISSIONS).map(([code, permissions]) => ({
                id: code,
                code,
                name: { admin: '管理员', manager: '项目经理', developer: '开发人员', tester: '测试人员', viewer: '查看者', member: '成员' }[code] || code,
                permissions: permissions.map(code => ({ id: code, code, name: code, category: '', description: '', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() })),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              })),
              loading: false
            });
            return;
          }

          // 从所有角色中提取唯一的权限
          const permissionMap = new Map<string, Permission>();
          const categoryMap: Record<string, string> = {
            'project': '项目管理',
            'task': '任务管理',
            'user': '用户管理',
            'role': '权限管理',
            'permission': '权限管理',
            'report': '报告管理',
            'issue': '问题管理',
            'resource': '资源管理',
            'team': '团队管理',
            'system': '系统管理',
          };

          for (const role of rolesData) {
            for (const permCode of role.permissions || []) {
              if (!permissionMap.has(permCode)) {
                const category = categoryMap[permCode.split(':')[0]] || '其他';
                permissionMap.set(permCode, {
                  id: permCode,
                  name: permCode.split(':')[1] || permCode,
                  code: permCode,
                  category,
                  description: `${category} - ${permCode}`,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                });
              }
            }
          }

          set({ permissions: Array.from(permissionMap.values()), loading: false });
        } catch (error) {
          set({ error: '获取权限失败', loading: false });
          console.error('获取权限失败:', error);
        }
      },

      createPermission: async (permission) => {
        set({ loading: true, error: null });
        try {
          // TODO: 调用 API 创建权限
          // 模拟数据
          const newPermission: Permission = {
            ...permission,
            id: String(Date.now()),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          set((state) => ({ permissions: [...state.permissions, newPermission], loading: false }));
          return newPermission;
        } catch (error) {
          set({ error: '创建权限失败', loading: false });
          console.error('创建权限失败:', error);
          throw error;
        }
      },

      updatePermission: async (id, permission) => {
        set({ loading: true, error: null });
        try {
          // TODO: 调用 API 更新权限
          // 模拟数据
          const updatedPermission: Permission = {
            ...get().permissions.find(p => p.id === id)!, 
            ...permission,
            updatedAt: new Date().toISOString(),
          };
          set((state) => ({
            permissions: state.permissions.map(p => p.id === id ? updatedPermission : p),
            loading: false,
          }));
          return updatedPermission;
        } catch (error) {
          set({ error: '更新权限失败', loading: false });
          console.error('更新权限失败:', error);
          throw error;
        }
      },

      deletePermission: async (id) => {
        set({ loading: true, error: null });
        try {
          // TODO: 调用 API 删除权限
          set((state) => ({
            permissions: state.permissions.filter(p => p.id !== id),
            loading: false,
          }));
        } catch (error) {
          set({ error: '删除权限失败', loading: false });
          console.error('删除权限失败:', error);
          throw error;
        }
      },

      // 角色管理
      fetchRoles: async () => {
        set({ loading: true, error: null });
        try {
          // 调用 API 获取角色列表
          const response = await api.get('/roles');
          let rolesData = response.data;

          // 处理可能的响应格式（data 可能在 data 字段中）
          if (rolesData && typeof rolesData === 'object' && !Array.isArray(rolesData) && Array.isArray(rolesData.data)) {
            rolesData = rolesData.data;
          }

          // 确保 rolesData 是数组
          if (!Array.isArray(rolesData)) {
            console.warn('Roles data is not an array, using fallback:', rolesData);
            // 使用本地定义的角色映射作为 fallback
            const fallbackRoles: Role[] = Object.entries(ROLE_PERMISSIONS).map(([code, permissions]) => ({
              id: code,
              code,
              name: { admin: '管理员', manager: '项目经理', developer: '开发人员', tester: '测试人员', viewer: '查看者', member: '成员' }[code] || code,
              description: `${{ admin: '管理员', manager: '项目经理', developer: '开发人员', tester: '测试人员', viewer: '查看者', member: '成员' }[code] || code} - 拥有 ${permissions.length} 个权限`,
              permissions: permissions.map(code => ({ id: code, code, name: code, category: '', description: '', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() })),
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }));
            set({ roles: fallbackRoles, loading: false });
            return;
          }

          const mappedRoles: Role[] = rolesData.map((r: any) => ({
            id: r.code,
            code: r.code,
            name: r.name,
            description: `${r.name} - 拥有 ${r.permission_count || 0} 个权限`,
            permissions: (r.permissions || []).map((code: string) => ({
              id: code,
              code,
              name: code.split(':')[1] || code,
              category: '',
              description: '',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            })),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }));

          set({ roles: mappedRoles, loading: false });
        } catch (error) {
          set({ error: '获取角色失败', loading: false });
          console.error('获取角色失败:', error);
        }
      },

      createRole: async (role) => {
        set({ loading: true, error: null });
        try {
          // TODO: 调用 API 创建角色
          // 模拟数据
          const newRole: Role = {
            ...role,
            id: String(Date.now()),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          set((state) => ({ roles: [...state.roles, newRole], loading: false }));
          return newRole;
        } catch (error) {
          set({ error: '创建角色失败', loading: false });
          console.error('创建角色失败:', error);
          throw error;
        }
      },

      updateRole: async (id, role) => {
        set({ loading: true, error: null });
        try {
          // TODO: 调用 API 更新角色
          // 模拟数据
          const updatedRole: Role = {
            ...get().roles.find(r => r.id === id)!, 
            ...role,
            updatedAt: new Date().toISOString(),
          };
          set((state) => ({
            roles: state.roles.map(r => r.id === id ? updatedRole : r),
            loading: false,
          }));
          return updatedRole;
        } catch (error) {
          set({ error: '更新角色失败', loading: false });
          console.error('更新角色失败:', error);
          throw error;
        }
      },

      deleteRole: async (id) => {
        set({ loading: true, error: null });
        try {
          // TODO: 调用 API 删除角色
          set((state) => ({
            roles: state.roles.filter(r => r.id !== id),
            loading: false,
          }));
        } catch (error) {
          set({ error: '删除角色失败', loading: false });
          console.error('删除角色失败:', error);
          throw error;
        }
      },

      // 用户角色管理
      fetchUserRoles: async () => {
        set({ loading: true, error: null });
        try {
          // TODO: 调用 API 获取用户角色关联
          // 模拟数据
          const mockUserRoles: UserRole[] = [
            { id: '1', userId: '1', roleId: '1', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
            { id: '2', userId: '2', roleId: '2', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
            { id: '3', userId: '3', roleId: '3', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
            { id: '4', userId: '4', roleId: '4', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
          ];
          set({ userRoles: mockUserRoles, loading: false });
        } catch (error) {
          set({ error: '获取用户角色失败', loading: false });
          console.error('获取用户角色失败:', error);
        }
      },

      assignRoleToUser: async (userId, roleId) => {
        set({ loading: true, error: null });
        try {
          // TODO: 调用 API 分配角色
          // 模拟数据
          const newUserRole: UserRole = {
            id: String(Date.now()),
            userId,
            roleId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          set((state) => ({ userRoles: [...state.userRoles, newUserRole], loading: false }));
          return newUserRole;
        } catch (error) {
          set({ error: '分配角色失败', loading: false });
          console.error('分配角色失败:', error);
          throw error;
        }
      },

      removeRoleFromUser: async (userId, roleId) => {
        set({ loading: true, error: null });
        try {
          // TODO: 调用 API 移除角色
          set((state) => ({
            userRoles: state.userRoles.filter(ur => !(ur.userId === userId && ur.roleId === roleId)),
            loading: false,
          }));
        } catch (error) {
          set({ error: '移除角色失败', loading: false });
          console.error('移除角色失败:', error);
          throw error;
        }
      },

      // 权限检查
      hasPermission: (permissionCode) => {
        const { user } = useAuthStore.getState();
        if (!user) return false;

        const userRole = user.role;
        const permissions = ROLE_PERMISSIONS[userRole] || ROLE_PERMISSIONS['viewer'] || [];
        return permissions.includes(permissionCode);
      },

      hasRole: (roleCode) => {
        const { user } = useAuthStore.getState();
        if (!user) return false;
        return user.role === roleCode;
      },

      // 重置状态
      reset: () => {
        set({
          permissions: [],
          roles: [],
          userRoles: [],
          loading: false,
          error: null,
        });
      },
    })
  )
);